import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import PhoneInput from "@/components/base/PhoneInput";
import { isInvalidWhatsapp, formatPhone } from "@/lib/phone";
import EventCheckin from "./EventCheckin";
import { generateCheckinToken } from "@/lib/checkin";

interface UpcomingEvent {
  id: number;
  title: string;
  description: string;
  image_url: string;
  event_datetime: string;
  location: string;
  max_spots: number;
  active: boolean;
  registrations_closed: boolean;
  unique_link_slug: string;
  created_at: string;
  checkin_token: string | null;
  checkin_opens_at: string | null;
  checkin_closes_at: string | null;
  exige_pagamento: boolean;
}

interface EventRegistration {
  id: number;
  event_id: number;
  member_id: number | null;
  name: string;
  email: string;
  whatsapp: string;
  country_code: string | null;
  city: string;
  profession: string;
  status: string;
  attendance_confirmed: boolean;
  created_at: string;
  approved_at: string | null;
  instagram: string;
  comprovante_path: string | null;
  comprovante_tipo: string | null;
  comprovante_recebido_em: string | null;
  payment_token: string | null;
  pagamento_confirmado_em: string | null;
  aviso_cancelamento_em: string | null;
  whatsapp_pre_reserva_em: string | null;
}

type ToastFn = (type: "success" | "error", message: string) => void;

interface Props {
  onToast: ToastFn;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");
}

function isPastDate(iso: string): boolean {
  return new Date(iso).getTime() < Date.now();
}

const EVENT_TIME_ZONE = "America/New_York";

function toDatetimeLocalNY(iso: string): string {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get("minute")}`;
}

function nyLocalToUtcIso(datetimeLocal: string): string {
  const [datePart, timePart] = datetimeLocal.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  const formatter = new Intl.DateTimeFormat("en-US", {
    timeZone: EVENT_TIME_ZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  let ts = Date.UTC(year, month - 1, day, hour, minute, 0);
  for (let i = 0; i < 2; i++) {
    const parts = formatter.formatToParts(new Date(ts));
    const getNum = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
    const zonedTs = Date.UTC(
      getNum("year"),
      getNum("month") - 1,
      getNum("day"),
      getNum("hour") % 24,
      getNum("minute"),
      getNum("second"),
    );
    ts += Date.UTC(year, month - 1, day, hour, minute, 0) - zonedTs;
  }
  return new Date(ts).toISOString();
}

function formatLocalTime12h(datetimeLocal: string): string {
  if (!datetimeLocal) return "";
  const [datePart, timePart] = datetimeLocal.split("T");
  if (!datePart || !timePart) return "";
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);
  const d = new Date(year, month - 1, day, hour, minute);
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
}

export default function UpcomingEventManager({ onToast }: Props) {
  const [events, setEvents] = useState<UpcomingEvent[]>([]);
  const [loading, setLoading] = useState(true);

  // Selected event for managing registrations
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [registrations, setRegistrations] = useState<EventRegistration[]>([]);
  const [regLoading, setRegLoading] = useState(false);

  // Registration status filter (null = all)
  const [statusFilter, setStatusFilter] = useState<"pending" | "approved" | "pagas" | "waitlist" | "rejected" | null>(null);

  // Edit mode
  const [editingEvent, setEditingEvent] = useState<UpcomingEvent | null>(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [checkinEventId, setCheckinEventId] = useState<number | null>(null);
  const [pendingSave, setPendingSave] = useState<{ payload: any; editId: number } | null>(null);
  const [pendingRegCount, setPendingRegCount] = useState(0);

  // Form fields
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formImageUrl, setFormImageUrl] = useState("");
  const [formDatetime, setFormDatetime] = useState("");
  const [formLocation, setFormLocation] = useState("");
  const [formMaxSpots, setFormMaxSpots] = useState(20);
  const [formSlug, setFormSlug] = useState("");
  const [formRegistrationsClosed, setFormRegistrationsClosed] = useState(false);
  const [formExigePagamento, setFormExigePagamento] = useState(false);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [correctionEvent, setCorrectionEvent] = useState<UpcomingEvent | null>(null);
  const [correctionCount, setCorrectionCount] = useState(0);
  const [correctionLoading, setCorrectionLoading] = useState(false);
  const [correctionSending, setCorrectionSending] = useState(false);

  // Edit registration mode
  const [editingReg, setEditingReg] = useState<EventRegistration | null>(null);
  const [regFormName, setRegFormName] = useState("");
  const [regFormEmail, setRegFormEmail] = useState("");
  const [regFormWhatsapp, setRegFormWhatsapp] = useState("");
  const [regFormCountryCode, setRegFormCountryCode] = useState("+1");
  const [regFormCity, setRegFormCity] = useState("");
  const [regFormProfession, setRegFormProfession] = useState("");
  const [regFormInstagram, setRegFormInstagram] = useState("");
  const [regFormError, setRegFormError] = useState("");
  const [updateMemberEmail, setUpdateMemberEmail] = useState(false);
  const [regSaving, setRegSaving] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  async function loadEvents() {
    setLoading(true);
    const { data, error } = await supabase
      .from("upcoming_events")
      .select("*")
      .order("event_datetime", { ascending: true });

    if (!error && data) {
      const loaded = data as UpcomingEvent[];
      // Fecha automaticamente eventos que já passaram, preservando o histórico no banco.
      const now = Date.now();
      const pastActive = loaded.filter(
        (e) => e.active && new Date(e.event_datetime).getTime() < now,
      );
      if (pastActive.length > 0) {
        const ids = pastActive.map((e) => e.id);
        await supabase
          .from("upcoming_events")
          .update({ active: false })
          .in("id", ids);
        pastActive.forEach((e) => {
          e.active = false;
        });
      }
      // o próximo a acontecer fica em cima; os que já passaram vão para o fim, do mais recente ao mais antigo
      const quando = (e: UpcomingEvent) => new Date(e.event_datetime).getTime();
      const futuros = loaded.filter((e) => quando(e) >= now).sort((a, b) => quando(a) - quando(b));
      const passados = loaded.filter((e) => quando(e) < now).sort((a, b) => quando(b) - quando(a));
      setEvents([...futuros, ...passados]);
    }
    setLoading(false);
  }

  // comprovante fica em bucket privado: link assinado válido por 5 minutos, só para a admin logada
  async function abrirComprovante(path: string) {
    const { data, error } = await supabase.storage.from("comprovantes").createSignedUrl(path, 300);
    if (error || !data) {
      onToast("error", "Não consegui abrir o comprovante.");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener");
  }

  // pré-reserva ('approved' sem pagamento) segura a vaga por 48 h a partir de approved_at
  function prazoPagamento(reg: EventRegistration) {
    if (!reg.approved_at) return null;
    const fim = new Date(new Date(reg.approved_at).getTime() + 48 * 3600e3);
    return { fim, vencido: fim.getTime() < Date.now() };
  }

  // a Ana conferiu o Zelle: a função grava a confirmação e avisa a inscrita (WhatsApp + e-mail)
  async function confirmarPagamento(reg: EventRegistration) {
    const { data, error } = await supabase.functions.invoke("confirmar-pagamento", { body: { registrationId: reg.id } });
    if (error || !data?.success) {
      onToast("error", data?.error || "Falha ao confirmar o pagamento.");
      return;
    }
    onToast(
      "success",
      data.avisos?.length
        ? `Pagamento de ${reg.name} confirmado, mas um aviso não saiu: ${data.avisos.join(" · ")}`
        : `Pagamento de ${reg.name} confirmado. Ela recebeu a confirmação por WhatsApp e e-mail.`,
    );
    loadRegistrations(reg.event_id);
  }

  // libera a vaga de quem não pagou (sem mensagem automática: a Ana fala com ela). A vaga volta a ficar livre
  // para pré-aprovar a próxima da lista de espera.
  async function cancelarPreReserva(reg: EventRegistration) {
    const { error } = await supabase.from("event_registrations").update({ status: "rejected" }).eq("id", reg.id);
    if (error) {
      onToast("error", "Falha ao cancelar a pré-reserva.");
      return;
    }
    const fila = registrations.filter((r) => r.status === "waitlist").length;
    onToast("success", fila ? `Pré-reserva cancelada. Há ${fila} na lista de espera: pré-aprove a próxima.` : "Pré-reserva cancelada. A vaga está livre.");
    loadRegistrations(reg.event_id);
  }

  // o WhatsApp da pré-reserva não saiu na hora de pré-aprovar: a Ana manda tentar de novo
  async function reenviarPreReserva(reg: EventRegistration) {
    const { data, error } = await supabase.functions.invoke("aprovar-whatsapp", { body: { registrationId: reg.id } });
    if (error || !data?.success) {
      onToast("error", `O WhatsApp de ${reg.name} ainda não saiu. Tente de novo em alguns minutos ou use "Copiar link de pagamento".`);
      return;
    }
    onToast("success", `Pré-reserva enviada para ${reg.name} pelo WhatsApp.`);
    loadRegistrations(reg.event_id);
  }

  // a Ana decide se avisa quem teve a pré-reserva cancelada (modelo pre_reserva_cancelada, uma vez)
  async function avisarCancelamento(reg: EventRegistration) {
    const { data, error } = await supabase.functions.invoke("whatsapp-evento", {
      body: { acao: "cancelada", registrationId: reg.id },
    });
    if (error || !data?.success) {
      onToast("error", data?.error || "O aviso de cancelamento não saiu. Tente de novo.");
      return;
    }
    onToast("success", `Aviso de cancelamento enviado para ${reg.name} pelo WhatsApp.`);
    loadRegistrations(reg.event_id);
  }

  // link de pagamento: a inscrita abre a página do evento com o token dela (ver /proximo-evento/:slug/pagamento)
  async function copiarLinkPagamento(reg: EventRegistration) {
    const event = events.find((e) => e.id === reg.event_id);
    if (!event || !reg.payment_token) {
      onToast("error", "Esta inscrição ainda não tem link de pagamento.");
      return;
    }
    const link = `${window.location.origin}/proximo-evento/${event.unique_link_slug}/pagamento?r=${reg.id}&t=${reg.payment_token}`;
    try {
      await navigator.clipboard.writeText(link);
      onToast("success", `Link de pagamento de ${reg.name} copiado.`);
    } catch {
      onToast("error", "Não consegui copiar. Link: " + link);
    }
  }

  async function loadRegistrations(eventId: number) {
    setRegLoading(true);
    setSelectedEventId(eventId);
    setStatusFilter(null);
    const { data, error } = await supabase
      .from("event_registrations")
      .select("*")
      .eq("event_id", eventId)
      .order("created_at", { ascending: false });

    if (!error && data) {
      setRegistrations(data as EventRegistration[]);
    }
    setRegLoading(false);
  }

  function openNewForm() {
    setEditingEvent(null);
    setShowNewForm(true);
    setFormTitle("");
    setFormDescription("");
    setFormImageUrl("");
    setFormDatetime("");
    setFormLocation("");
    setFormMaxSpots(20);
    setFormSlug("");
    setFormRegistrationsClosed(false);
    setFormExigePagamento(false);
  }

  function openEditForm(event: UpcomingEvent) {
    setShowNewForm(false);
    setEditingEvent(event);
    setFormTitle(event.title);
    setFormDescription(event.description || "");
    setFormImageUrl(event.image_url || "");
    setFormDatetime(event.event_datetime ? toDatetimeLocalNY(event.event_datetime) : "");
    setFormLocation(event.location || "");
    setFormMaxSpots(event.max_spots);
    setFormSlug(event.unique_link_slug);
    setFormRegistrationsClosed(event.registrations_closed);
    setFormExigePagamento(!!event.exige_pagamento);
  }

  function closeForm() {
    setEditingEvent(null);
    setShowNewForm(false);
  }

  async function ensureBucketExists() {
    try {
      const { data: buckets } = await supabase.storage.listBuckets();
      const exists = buckets?.some((b) => b.name === "event-images");
      if (!exists) {
        await supabase.storage.createBucket("event-images", {
          public: true,
          fileSizeLimit: 52428800,
          allowedMimeTypes: null,
        });
      } else {
        await supabase.storage.updateBucket("event-images", {
          public: true,
          fileSizeLimit: 52428800,
          allowedMimeTypes: null,
        });
      }
    } catch {
      // bucket pode já existir, ignora erro de permissão
    }
  }

  async function uploadImage(file: File): Promise<string | null> {
    if (!file) return null;
    try {
      setUploading(true);
      await ensureBucketExists();

      const filePath = `upcoming-events/${Date.now()}-${file.name.replace(/\s+/g, "_")}`;

      const { error: uploadError } = await supabase.storage
        .from("event-images")
        .upload(filePath, file, {
          contentType: file.type || "image/jpeg",
          upsert: false,
        });

      if (uploadError) {
        onToast("error", "Falha no upload: " + uploadError.message);
        return null;
      }

      const { data: publicUrlData } = supabase.storage
        .from("event-images")
        .getPublicUrl(filePath);

      return publicUrlData.publicUrl;
    } catch (err: any) {
      onToast("error", "Erro ao enviar arquivo: " + (err?.message || "Erro desconhecido"));
      return null;
    } finally {
      setUploading(false);
    }
  }

  async function handleImageUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) {
      setFormImageUrl(url);
      onToast("success", "Imagem enviada com sucesso!");
    }
  }

  async function handleSaveEvent(e: React.FormEvent) {
    e.preventDefault();
    if (!formTitle.trim() || !formDatetime) {
      onToast("error", "Título e data/hora são obrigatórios.");
      return;
    }

    const slug = formSlug || slugify(formTitle);
    const newDatetimeIso = nyLocalToUtcIso(formDatetime);
    const payload: any = {
      title: formTitle.trim(),
      description: formDescription.trim(),
      image_url: formImageUrl.trim() || null,
      event_datetime: newDatetimeIso,
      location: formLocation.trim(),
      max_spots: formMaxSpots,
      unique_link_slug: slug,
      registrations_closed: formRegistrationsClosed,
      exige_pagamento: formExigePagamento,
    };

    if (editingEvent) {
      const dateChanged = newDatetimeIso !== editingEvent.event_datetime;
      if (dateChanged) {
        setFormSubmitting(true);
        const { count } = await supabase
          .from("event_registrations")
          .select("*", { count: "exact", head: true })
          .eq("event_id", editingEvent.id);
        setFormSubmitting(false);
        if ((count ?? 0) > 0) {
          setPendingSave({ payload, editId: editingEvent.id });
          setPendingRegCount(count ?? 0);
          return;
        }
      }
      await performSave(payload, editingEvent.id);
    } else {
      payload.checkin_token = generateCheckinToken();
      payload.checkin_opens_at = new Date(new Date(newDatetimeIso).getTime() - 3 * 3600 * 1000).toISOString();
      payload.checkin_closes_at = new Date(new Date(newDatetimeIso).getTime() + 6 * 3600 * 1000).toISOString();
      await performSave(payload, null);
    }
  }

  async function performSave(payload: any, editId: number | null) {
    setFormSubmitting(true);
    if (editId) {
      const { error } = await supabase
        .from("upcoming_events")
        .update(payload)
        .eq("id", editId);

      if (error) {
        onToast("error", "Falha ao atualizar evento.");
      } else {
        onToast("success", "Evento atualizado com sucesso!");
        closeForm();
        loadEvents();
      }
    } else {
      const { error } = await supabase
        .from("upcoming_events")
        .insert(payload);

      if (error) {
        onToast("error", "Falha ao criar evento: " + (error.message || ""));
      } else {
        onToast("success", "Evento criado com sucesso!");
        closeForm();
        loadEvents();
      }
    }
    setFormSubmitting(false);
  }

  async function confirmPendingSave() {
    if (!pendingSave) return;
    setPendingSave(null);
    await performSave(pendingSave.payload, pendingSave.editId);
  }

  async function toggleActive(eventId: number, currentActive: boolean) {
    const { error } = await supabase
      .from("upcoming_events")
      .update({ active: !currentActive })
      .eq("id", eventId);

    if (!error) {
      setEvents((prev) =>
        prev.map((e) => (e.id === eventId ? { ...e, active: !currentActive } : e)),
      );
      onToast("success", `Evento ${!currentActive ? "ativado" : "desativado"}.`);
    }
  }

  async function approveRegistration(regId: number) {
    const reg = registrations.find((r) => r.id === regId);
    if (!reg) return;

    const event = events.find((e) => e.id === selectedEventId);
    if (!event) return;

    // pré-reserva conta como vaga ocupada: com todas ocupadas, só depois de cancelar uma
    const approvedCount = registrations.filter((r) => r.status === "approved").length;
    if (event.max_spots > 0 && approvedCount >= event.max_spots) {
      onToast("error", `As ${event.max_spots} vagas estão ocupadas. Cancele uma pré-reserva vencida para liberar uma vaga.`);
      return;
    }

    // Update registration status
    const { error: regErr } = await supabase
      .from("event_registrations")
      .update({ status: "approved", approved_at: new Date().toISOString() })
      .eq("id", regId);

    if (regErr) {
      onToast("error", "Falha ao aprovar inscrição.");
      return;
    }

    // If member exists, update member status to active
    if (reg.member_id) {
      await supabase
        .from("members")
        .update({ status: "active" })
        .eq("id", reg.member_id);
    }

    // Não fecha mais as inscrições ao lotar: quem chega depois entra na lista de espera (o banco decide o status).

    // Reload registrations
    loadRegistrations(selectedEventId!);

    // Send approval confirmation email
    let whatsappFalhou = false;
    try {
      const eventDateStr = `${new Date(event.event_datetime).toLocaleDateString("pt-BR", {
        weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "America/New_York",
      })} às ${new Date(event.event_datetime).toLocaleTimeString("en-US", {
        hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/New_York",
      })}`;

      // WhatsApp automático de pré-reserva (modelo vaga_pre_reservada, com o link de pagamento)
      // (só em evento pago: o modelo traz os dados do Zelle)
      let aprovada: { payment_token: string | null } | null = null;
      if (event.exige_pagamento) {
        const zap = await supabase.functions.invoke("aprovar-whatsapp", { body: { registrationId: regId } });
        if (zap.error || !zap.data?.success) {
          whatsappFalhou = true;
          console.error("WhatsApp de aprovação não enviado:", zap.error);
        }

        // o token de pagamento é gerado pelo banco na aprovação; lê de novo para montar o link
        ({ data: aprovada } = await supabase
          .from("event_registrations")
          .select("payment_token")
          .eq("id", regId)
          .single());
      }
      const paymentLink = aprovada?.payment_token
        ? `${window.location.origin}/proximo-evento/${event.unique_link_slug}/pagamento?r=${regId}&t=${aprovada.payment_token}`
        : undefined;

      await supabase.functions.invoke("send-approval-email", {
        body: {
          paymentLink,
          email: reg.email,
          name: reg.name,
          eventType: "event",
          eventTitle: event.title,
          eventDate: eventDateStr,
          eventLocation: event.location || "",
          baseUrl: window.location.origin,
        },
      });
    } catch {
      // non-critical
    }

    loadRegistrations(selectedEventId!);
    if (whatsappFalhou) {
      onToast("error", "Pré-aprovada, mas o WhatsApp NÃO saiu. Use o botão \"Tentar WhatsApp de novo\" na inscrição.");
      return;
    }
    onToast("success", event.exige_pagamento ? "Pré-reserva enviada: ela tem 48 horas para pagar." : "Inscrição aprovada com sucesso!");
  }

  async function rejectRegistration(regId: number) {
    const reg = registrations.find((r) => r.id === regId);
    if (!reg) return;

    const event = events.find((e) => e.id === selectedEventId);
    if (!event) return;

    const { error: regErr } = await supabase
      .from("event_registrations")
      .update({ status: "rejected" })
      .eq("id", regId);

    if (regErr) {
      onToast("error", "Falha ao recusar inscrição.");
      return;
    }

    // Send rejection email
    try {
      await supabase.functions.invoke("notify-event-rejection", {
        body: {
          name: reg.name,
          email: reg.email,
          eventTitle: event.title,
        },
      });

      // WhatsApp de recusa (modelo inscricao_recusada)
      const zap = await supabase.functions.invoke("whatsapp-evento", { body: { acao: "recusada", registrationId: reg.id } });
      if (zap.error) console.error("WhatsApp de recusa não enviado:", zap.error);
    } catch {
      // non-critical
    }

    onToast("success", "Inscrição recusada. E-mail enviado.");
    loadRegistrations(selectedEventId!);
  }

  async function openCorrectionDialog(event: UpcomingEvent) {
    setCorrectionEvent(event);
    setCorrectionCount(0);
    setCorrectionLoading(true);
    const { data } = await supabase
      .from("event_registrations")
      .select("id")
      .eq("event_id", event.id)
      .eq("status", "approved")
      .not("attendance_token", "is", null);
    setCorrectionCount(data?.length ?? 0);
    setCorrectionLoading(false);
  }

  async function confirmSendCorrection() {
    if (!correctionEvent) return;
    setCorrectionSending(true);
    const { error } = await supabase.functions.invoke("send-event-time-correction", {
      body: { eventId: correctionEvent.id },
    });
    setCorrectionSending(false);
    setCorrectionEvent(null);
    if (error) {
      onToast("error", "Falha ao enviar correção.");
    } else {
      onToast("success", `Correção de horário enviada para ${correctionCount} inscrita(s).`);
    }
  }

  async function resendReminder(reg: EventRegistration) {
    const { error } = await supabase.functions.invoke("send-event-reminder", {
      body: { eventId: reg.event_id, registrationId: reg.id },
    });
    if (error) {
      onToast("error", "Falha ao reenviar lembrete.");
    } else {
      onToast("success", `Lembrete reenviado para ${reg.name}.`);
    }
  }

  function isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  }

  function openEditRegistration(reg: EventRegistration) {
    setEditingReg(reg);
    setRegFormName(reg.name || "");
    setRegFormEmail(reg.email || "");
    setRegFormWhatsapp(formatPhone(reg.whatsapp || "", reg.country_code || "+1"));
    setRegFormCountryCode(reg.country_code || "+1");
    setRegFormCity(reg.city || "");
    setRegFormProfession(reg.profession || "");
    setRegFormInstagram(reg.instagram || "");
    setRegFormError("");
    setUpdateMemberEmail(false);
  }

  function closeEditRegistration() {
    setEditingReg(null);
    setRegFormError("");
    setUpdateMemberEmail(false);
  }

  async function handleSaveRegistration(e: React.FormEvent) {
    e.preventDefault();

    const name = regFormName.trim();
    const email = regFormEmail.trim();

    if (!name || !email) {
      setRegFormError("Nome e e-mail são obrigatórios.");
      return;
    }
    if (!isValidEmail(email)) {
      setRegFormError("E-mail inválido. Verifique o formato (ex.: nome@dominio.com).");
      return;
    }

    if (!editingReg) return;

    setRegSaving(true);
    setRegFormError("");

    const originalEmail = (editingReg.email || "").trim().toLowerCase();
    const emailChanged = email.toLowerCase() !== originalEmail;

    const payload = {
      name,
      email,
      whatsapp: regFormWhatsapp.replace(/\D/g, ""),
      country_code: regFormCountryCode,
      city: regFormCity.trim(),
      profession: regFormProfession.trim(),
      instagram: regFormInstagram.trim(),
    };

    const { error: regErr } = await supabase
      .from("event_registrations")
      .update(payload)
      .eq("id", editingReg.id);

    if (regErr) {
      setRegFormError("Falha ao atualizar inscrição: " + (regErr.message || ""));
      setRegSaving(false);
      return;
    }

    // If linked to a member and email changed and admin chose to sync
    if (editingReg.member_id && emailChanged && updateMemberEmail) {
      const { error: memberErr } = await supabase
        .from("members")
        .update({ email })
        .eq("id", editingReg.member_id);

      if (memberErr) {
        onToast("error", "Inscrição atualizada, mas falha ao atualizar e-mail do membro: " + (memberErr.message || ""));
      }
    }

    setRegSaving(false);
    closeEditRegistration();
    onToast("success", "Inscrição atualizada com sucesso!");
    if (selectedEventId) {
      loadRegistrations(selectedEventId);
    }
  }

  function copyLink(slug: string) {
    const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
    const pathPrefix = basePath ? `/${basePath}` : "";
    const link = `${window.location.origin}${pathPrefix}/proximo-evento/${slug}`;
    navigator.clipboard.writeText(link);
    onToast("success", "Link copiado!");
  }

  // Counts
  const selectedEvent = events.find((e) => e.id === selectedEventId);
  const checkinEvent = events.find((e) => e.id === checkinEventId);
  const editingRegEmailChanged =
    editingReg && editingReg.member_id
      ? regFormEmail.trim().toLowerCase() !== (editingReg.email || "").trim().toLowerCase()
      : false;
  const pendingCount = registrations.filter((r) => r.status === "pending").length;
  const approvedCount = registrations.filter((r) => r.status === "approved").length;
  const pagasCount = registrations.filter((r) => r.status === "approved" && r.pagamento_confirmado_em).length;
  const waitlistCount = registrations.filter((r) => r.status === "waitlist").length;
  const rejectedCount = registrations.filter((r) => r.status === "rejected").length;
  const lotado = !!selectedEvent && selectedEvent.max_spots > 0 && approvedCount >= selectedEvent.max_spots;
  const eventoPago = !!selectedEvent?.exige_pagamento;

  const filteredRegistrations = !statusFilter
    ? registrations
    : statusFilter === "pagas"
      ? registrations.filter((r) => r.status === "approved" && r.pagamento_confirmado_em)
      : statusFilter === "approved"
        ? registrations.filter((r) => r.status === "approved" && !r.pagamento_confirmado_em)
        : statusFilter === "waitlist"
          // lista de espera na ordem de chegada
          ? registrations.filter((r) => r.status === "waitlist").sort((x, y) => x.created_at.localeCompare(y.created_at))
          : registrations.filter((r) => r.status === statusFilter);

  return (
    <div>
      {/* Uploading overlay */}
      {uploading && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center">
          <div className="bg-white rounded-2xl p-8 flex flex-col items-center gap-4">
            <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
            <p className="font-body text-sm text-brown">Enviando imagem...</p>
          </div>
        </div>
      )}

      {/* Correction confirmation dialog */}
      {correctionEvent && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full">
            <h3 className="font-display text-lg text-brown mb-3">Enviar correção de horário</h3>
            {correctionLoading ? (
              <div className="flex items-center gap-3 py-6">
                <div className="w-6 h-6 border-2 border-primary/20 border-t-primary rounded-full animate-spin" />
                <p className="font-body text-sm text-brown/60">Verificando inscritas...</p>
              </div>
            ) : (
              <>
                <p className="font-body text-sm text-brown/70 mb-4">
                  Você está prestes a enviar o e-mail de correção de horário para{" "}
                  <strong className="text-primary">{correctionCount}</strong> inscrita(s) aprovada(s) que já receberam o lembrete.
                  Nenhum e-mail será enviado até você confirmar.
                </p>
                <div className="flex items-center gap-3">
                  <button
                    onClick={confirmSendCorrection}
                    disabled={correctionSending || correctionCount === 0}
                    className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                  >
                    {correctionSending ? "Enviando..." : "Confirmar envio"}
                  </button>
                  <button
                    onClick={() => setCorrectionEvent(null)}
                    className="px-5 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Cancelar
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Confirm date-change dialog */}
      {pendingSave && (
        <div className="fixed inset-0 z-[75] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-md w-full">
            <h3 className="font-display text-lg text-brown mb-3">Alterar data de um evento com inscrições</h3>
            <p className="font-body text-sm text-brown/70 mb-4 leading-relaxed">
              Este evento já tem <strong className="text-primary">{pendingRegCount}</strong> inscrição(ões).
              Se você alterar a data, elas continuarão ligadas a este evento e passarão a apontar para a nova data.
              Se for um encontro diferente, o ideal é <strong>criar um evento novo</strong>.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={confirmPendingSave}
                className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Alterar mesmo assim
              </button>
              <button
                onClick={() => setPendingSave(null)}
                className="px-5 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit registration dialog */}
      {editingReg && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 md:p-8 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="font-display text-lg text-brown mb-1">Editar inscrição</h3>
            <p className="font-body text-sm text-brown/50 mb-5">
              {editingReg.name} · inscrição #{editingReg.id}
            </p>
            <form onSubmit={handleSaveRegistration} className="space-y-4">
              <div>
                <label className="font-body text-sm text-brown block mb-2">Nome *</label>
                <input
                  type="text"
                  value={regFormName}
                  onChange={(e) => setRegFormName(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">E-mail *</label>
                <input
                  type="email"
                  value={regFormEmail}
                  onChange={(e) => setRegFormEmail(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">WhatsApp</label>
                <PhoneInput
                  countryCode={regFormCountryCode}
                  whatsapp={regFormWhatsapp}
                  onChange={({ countryCode, whatsapp }) => {
                    setRegFormCountryCode(countryCode);
                    setRegFormWhatsapp(whatsapp);
                  }}
                  disabled={regSaving}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="font-body text-sm text-brown block mb-2">Cidade</label>
                  <input
                    type="text"
                    value={regFormCity}
                    onChange={(e) => setRegFormCity(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
                <div>
                  <label className="font-body text-sm text-brown block mb-2">Profissão</label>
                  <input
                    type="text"
                    value={regFormProfession}
                    onChange={(e) => setRegFormProfession(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">Instagram</label>
                <input
                  type="text"
                  value={regFormInstagram}
                  onChange={(e) => setRegFormInstagram(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                />
              </div>

              {editingRegEmailChanged && (
                <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
                  <p className="font-body text-sm text-amber-800 mb-2">
                    O e-mail foi alterado e esta inscrição está vinculada a um membro. Deseja atualizar também o e-mail do membro correspondente?
                  </p>
                  <label className="flex items-start gap-2 mb-1.5 cursor-pointer">
                    <input
                      type="radio"
                      name="memberEmailChoice"
                      checked={!updateMemberEmail}
                      onChange={() => setUpdateMemberEmail(false)}
                      className="mt-1 accent-primary"
                    />
                    <span className="font-body text-sm text-brown">Atualizar apenas a inscrição</span>
                  </label>
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="memberEmailChoice"
                      checked={updateMemberEmail}
                      onChange={() => setUpdateMemberEmail(true)}
                      className="mt-1 accent-primary"
                    />
                    <span className="font-body text-sm text-brown">Atualizar também o e-mail do membro</span>
                  </label>
                </div>
              )}

              {regFormError && (
                <p className="font-body text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {regFormError}
                </p>
              )}

              <div className="flex items-center gap-3 pt-2">
                <button
                  type="submit"
                  disabled={regSaving}
                  className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {regSaving ? "Salvando..." : "Salvar"}
                </button>
                <button
                  type="button"
                  onClick={closeEditRegistration}
                  className="px-5 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Top bar: create event + event selector */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <button
          onClick={openNewForm}
          className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
        >
          <div className="w-4 h-4 flex items-center justify-center">
            <i className="ri-add-line" />
          </div>
          Novo Evento
        </button>

        {/* Event dropdown selector */}
        <div className="flex items-center gap-2">
          <span className="font-body text-sm text-brown/60 whitespace-nowrap">Ver inscrições:</span>
          <select
            value={selectedEventId || ""}
            onChange={(e) => {
              const val = e.target.value;
              if (val) {
                loadRegistrations(Number(val));
              } else {
                setSelectedEventId(null);
                setRegistrations([]);
              }
            }}
            className="px-4 py-2.5 rounded-lg border border-nude bg-white font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors cursor-pointer"
          >
            <option value="">Selecionar evento...</option>
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title} {ev.active ? "" : "(inativo)"}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* New/Edit event form */}
      {(showNewForm || editingEvent) && (
        <div className="bg-white rounded-2xl p-6 md:p-8 mb-8">
          <h3 className="font-display text-lg text-brown mb-6">
            {editingEvent ? "Editar evento" : "Novo evento"}
          </h3>
          <form onSubmit={handleSaveEvent} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="font-body text-sm text-brown block mb-2">Título *</label>
                <input
                  type="text"
                  value={formTitle}
                  onChange={(e) => {
                    setFormTitle(e.target.value);
                    if (!editingEvent && !formSlug) {
                      setFormSlug(slugify(e.target.value));
                    }
                  }}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">Data e Hora *</label>
                <input
                  type="datetime-local"
                  value={formDatetime}
                  onChange={(e) => setFormDatetime(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                />
                {formDatetime && (
                  <p className="font-body text-xs text-brown/50 mt-1.5">
                    às {formatLocalTime12h(formDatetime)}
                  </p>
                )}
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">Local</label>
                <input
                  type="text"
                  value={formLocation}
                  onChange={(e) => setFormLocation(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div>
                <label className="font-body text-sm text-brown block mb-2">Vagas máximas</label>
                <input
                  type="number"
                  value={formMaxSpots}
                  onChange={(e) => setFormMaxSpots(Number(e.target.value))}
                  min={1}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown focus:outline-none focus:border-primary transition-colors"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-sm text-brown block mb-2">URL da imagem</label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formImageUrl}
                      onChange={(e) => setFormImageUrl(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                      placeholder="https://..."
                    />
                  </div>
                  <label className="flex items-center gap-2 px-4 py-3 rounded-lg bg-primary/10 text-primary font-body text-sm hover:bg-primary/20 transition-colors cursor-pointer whitespace-nowrap">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-upload-cloud-line" />
                    </div>
                    <span>Enviar arquivo</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                    />
                  </label>
                </div>
                {formImageUrl && (
                  <div className="mt-3 w-32 h-32 rounded-xl overflow-hidden">
                    <img
                      src={formImageUrl}
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                  </div>
                )}
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-sm text-brown block mb-2">Slug do link</label>
                <input
                  type="text"
                  value={formSlug}
                  onChange={(e) => setFormSlug(e.target.value)}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors"
                  placeholder="meu-evento"
                />
              </div>
              <div className="md:col-span-2">
                <label className="font-body text-sm text-brown block mb-2">Descrição</label>
                <textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-3 rounded-lg border border-nude bg-beige/50 font-body text-sm text-brown placeholder-brown/40 focus:outline-none focus:border-primary transition-colors resize-none"
                />
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-beige/60 border border-nude">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <i className="ri-money-dollar-circle-line text-primary" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-brown font-semibold">Evento pago</p>
                      <p className="font-body text-xs text-brown/60 mt-0.5">
                        Ligado: pré-aprovar segura a vaga por 48 h, a inscrita recebe os dados do Zelle e só fica confirmada depois do pagamento.
                        Lembretes vão só para quem pagou.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormExigePagamento(!formExigePagamento)}
                    className={`relative w-12 h-7 rounded-full transition-colors shrink-0 cursor-pointer ${formExigePagamento ? "bg-primary" : "bg-brown/20"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform duration-200 ${formExigePagamento ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-beige/60 border border-nude">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                      <i className="ri-lock-line text-primary" />
                    </div>
                    <div>
                      <p className="font-body text-sm text-brown font-semibold">Inscrições encerradas</p>
                      <p className="font-body text-xs text-brown/60 mt-0.5">
                        Ao ligar, o botão "Quero participar" é substituído por um aviso no site.
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormRegistrationsClosed(!formRegistrationsClosed)}
                    className={`relative w-12 h-7 rounded-full transition-colors shrink-0 cursor-pointer ${formRegistrationsClosed ? "bg-primary" : "bg-brown/20"}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-6 h-6 rounded-full bg-white transition-transform duration-200 ${formRegistrationsClosed ? "translate-x-5" : ""}`} />
                  </button>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 pt-2">
              <button
                type="submit"
                disabled={formSubmitting}
                className="px-5 py-2.5 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {formSubmitting ? "Salvando..." : editingEvent ? "Atualizar" : "Criar evento"}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="px-5 py-2.5 rounded-full bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Check-in view */}
      {checkinEventId && checkinEvent && (
        <div>
          <button
            onClick={() => setCheckinEventId(null)}
            className="flex items-center gap-1.5 text-brown/60 hover:text-brown font-body text-sm transition-colors mb-6 cursor-pointer"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-left-line" />
            </div>
            Voltar para lista de eventos
          </button>
          <EventCheckin event={checkinEvent} onToast={onToast} />
        </div>
      )}

      {/* Events list */}
      {!selectedEventId && !checkinEventId && (
        <>
          {loading ? (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
              <p className="font-body text-sm text-brown/60">Carregando eventos...</p>
            </div>
          ) : events.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <i className="ri-calendar-event-line text-4xl text-brown/20 mb-4" />
              <p className="font-body text-brown/60">Nenhum evento futuro cadastrado.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {events.map((event) => (
                <div key={event.id} className="bg-white rounded-2xl p-5 md:p-6">
                  {/* botões ocupam a linha de baixo: lado a lado, espremiam o texto do evento em uma coluna estreita */}
                  <div className="flex flex-wrap gap-4 items-start">
                    {event.image_url && (
                      <img
                        src={event.image_url}
                        alt={event.title}
                        className="w-20 h-20 rounded-xl object-cover object-top shrink-0"
                      />
                    )}
                    <div className="flex-1 min-w-[200px]">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="font-display text-base text-brown">{event.title}</h4>
                        <span className={`px-2.5 py-0.5 rounded-full font-body text-xs uppercase tracking-wide ${
                          isPastDate(event.event_datetime)
                            ? "bg-brown/10 text-brown/50"
                            : event.active
                              ? "bg-green-100 text-green-700"
                              : "bg-brown/10 text-brown/40"
                        }`}>
                          {isPastDate(event.event_datetime) ? "Encerrado" : event.active ? "Ativo" : "Inativo"}
                        </span>
                        {event.registrations_closed && (
                          <span className="px-2.5 py-0.5 rounded-full bg-primary/15 text-primary font-body text-xs uppercase tracking-wide">
                            Inscrições encerradas
                          </span>
                        )}
                      </div>
                      <p className="font-body text-sm text-primary mb-2">
                        {new Date(event.event_datetime).toLocaleDateString("pt-BR", {
                          weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "America/New_York",
                        })} às {new Date(event.event_datetime).toLocaleTimeString("en-US", {
                          hour: "numeric", minute: "2-digit", hour12: true, timeZone: "America/New_York",
                        })}
                      </p>
                      <p className="font-body text-sm text-brown/60 mb-1">{event.location}</p>
                      <p className="font-body text-xs text-brown/40">{event.max_spots} vagas</p>
                    </div>
                    <div className="flex flex-wrap gap-2 w-full">
                      <button
                        onClick={() => loadRegistrations(event.id)}
                        className="px-3 py-2 rounded-lg bg-primary/10 text-primary font-body text-xs hover:bg-primary/20 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-group-line" />
                        </div>
                        Inscrições
                      </button>
                      <button
                        onClick={() => setCheckinEventId(event.id)}
                        className="px-3 py-2 rounded-lg bg-secondary-100 text-secondary-900 font-body text-xs hover:bg-secondary-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-qr-code-line" />
                        </div>
                        Check-in
                      </button>
                      <button
                        onClick={() => openCorrectionDialog(event)}
                        className="px-3 py-2 rounded-lg bg-orange-100 text-orange-700 font-body text-xs hover:bg-orange-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-time-line" />
                        </div>
                        Enviar correção de horário
                      </button>
                      <button
                        onClick={() => openEditForm(event)}
                        className="px-3 py-2 rounded-lg bg-amber-100 text-amber-700 font-body text-xs hover:bg-amber-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-edit-line" />
                        </div>
                        Editar
                      </button>
                      <button
                        onClick={() => copyLink(event.unique_link_slug)}
                        className="px-3 py-2 rounded-lg bg-accent-100 text-accent-700 font-body text-xs hover:bg-accent-200 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className="ri-link" />
                        </div>
                        Copiar link
                      </button>
                      <button
                        onClick={() => toggleActive(event.id, event.active)}
                        className={`px-3 py-2 rounded-lg font-body text-xs transition-colors whitespace-nowrap cursor-pointer ${
                          event.active
                            ? "bg-brown/10 text-brown/60 hover:bg-brown/20"
                            : "bg-green-100 text-green-700 hover:bg-green-200"
                        }`}
                      >
                        <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                          <i className={event.active ? "ri-eye-off-line" : "ri-eye-line"} />
                        </div>
                        {event.active ? "Desativar" : "Ativar"}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* Registrations view */}
      {selectedEventId && !checkinEventId && selectedEvent && (
        <div>
          {/* Back button and summary */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <button
              onClick={() => { setSelectedEventId(null); setRegistrations([]); }}
              className="flex items-center gap-1.5 text-brown/60 hover:text-brown font-body text-sm transition-colors cursor-pointer"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line" />
              </div>
              Voltar para lista de eventos
            </button>
            <div className="flex items-center gap-4 flex-wrap">
              <span className="font-body text-sm text-brown/60">
                <strong className="text-primary">{approvedCount}</strong>/{selectedEvent.max_spots} vagas ocupadas · <strong className="text-green-700">{pagasCount}</strong> pagas
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setStatusFilter(null)}
                  className={`px-2.5 py-1 rounded-full font-body text-xs border transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === null
                      ? "bg-brown/15 text-brown border-brown/40 font-semibold"
                      : "bg-brown/5 text-brown/50 border-transparent"
                  }`}
                >
                  Todas
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === "pending" ? null : "pending")}
                  className={`px-2.5 py-1 rounded-full font-body text-xs border transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === "pending"
                      ? "bg-yellow-100 text-yellow-800 border-yellow-400 font-semibold"
                      : "bg-yellow-100/50 text-yellow-700/50 border-transparent"
                  }`}
                >
                  {pendingCount} pendentes
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === "approved" ? null : "approved")}
                  className={`px-2.5 py-1 rounded-full font-body text-xs border transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === "approved"
                      ? "bg-amber-100 text-amber-800 border-amber-400 font-semibold"
                      : "bg-amber-100/50 text-amber-700/60 border-transparent"
                  }`}
                >
                  {approvedCount - pagasCount} pré-reservadas
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === "pagas" ? null : "pagas")}
                  className={`px-2.5 py-1 rounded-full font-body text-xs border transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === "pagas"
                      ? "bg-emerald-600 text-white border-emerald-700 font-semibold"
                      : "bg-emerald-100/60 text-emerald-700/70 border-transparent"
                  }`}
                >
                  {pagasCount} confirmadas
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === "waitlist" ? null : "waitlist")}
                  className={`px-2.5 py-1 rounded-full font-body text-xs border transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === "waitlist"
                      ? "bg-indigo-100 text-indigo-800 border-indigo-400 font-semibold"
                      : "bg-indigo-100/50 text-indigo-700/60 border-transparent"
                  }`}
                >
                  {waitlistCount} na lista de espera
                </button>
                <button
                  onClick={() => setStatusFilter(statusFilter === "rejected" ? null : "rejected")}
                  className={`px-2.5 py-1 rounded-full font-body text-xs border transition-colors whitespace-nowrap cursor-pointer ${
                    statusFilter === "rejected"
                      ? "bg-red-100 text-red-800 border-red-400 font-semibold"
                      : "bg-red-100/50 text-red-700/50 border-transparent"
                  }`}
                >
                  {rejectedCount} recusadas
                </button>
              </div>
            </div>
          </div>

          {/* Spots warning */}
          {lotado && (
            <div className="mb-6 px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 flex items-start gap-3">
              <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                <i className="ri-alert-line text-amber-600 text-sm" />
              </div>
              <p className="font-body text-sm text-amber-800">
                As {selectedEvent.max_spots} vagas estão ocupadas (pré-reservadas ou pagas). Quem se inscrever agora entra na lista de espera.
                Se uma pré-reserva vencer sem pagamento, cancele-a e pré-aprove a primeira da lista de espera.
              </p>
            </div>
          )}

          <h3 className="font-display text-lg text-brown mb-4">
            Inscrições: {selectedEvent.title}
          </h3>

          {regLoading ? (
            <div className="text-center py-12">
              <div className="w-10 h-10 border-2 border-primary/20 border-t-primary rounded-full animate-spin mx-auto mb-4" />
            </div>
          ) : registrations.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <i className="ri-user-search-line text-4xl text-brown/20 mb-4" />
              <p className="font-body text-brown/60">Nenhuma inscrição neste evento ainda.</p>
            </div>
          ) : filteredRegistrations.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <i className="ri-inbox-line text-4xl text-brown/20 mb-4" />
              <p className="font-body text-brown/60">
                Nenhuma inscrita com esse status por aqui no momento.
              </p>
              <button
                onClick={() => setStatusFilter(null)}
                className="mt-4 px-4 py-2 rounded-full bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer"
              >
                Ver todas as inscritas
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredRegistrations.map((reg) => (
                <div key={reg.id} className={`bg-white rounded-2xl p-5 ${reg.status === "rejected" ? "opacity-60" : ""}`}>
                  <div className="flex flex-col md:flex-row gap-4 items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-1">
                        <h4 className="font-display text-base text-brown">{reg.name}</h4>
                        {reg.comprovante_path && (
                          <button
                            type="button"
                            onClick={() => abrirComprovante(reg.comprovante_path as string)}
                            className="px-2.5 py-0.5 rounded-full bg-green-600 text-white font-body text-xs uppercase tracking-wide hover:bg-green-700 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            Comprovante {reg.comprovante_tipo === "image" ? "(foto)" : "(PDF)"}
                          </button>
                        )}
                        {reg.status === "pending" && (
                          <span className="px-2.5 py-0.5 rounded-full bg-yellow-100 text-yellow-700 font-body text-xs uppercase tracking-wide">Pendente</span>
                        )}
                        {reg.status === "approved" && eventoPago && !reg.pagamento_confirmado_em && !reg.whatsapp_pre_reserva_em && (
                          <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-body text-xs uppercase tracking-wide">WhatsApp não enviado</span>
                        )}
                        {reg.status === "approved" && !eventoPago && (
                          <span className="px-2.5 py-0.5 rounded-full bg-green-100 text-green-700 font-body text-xs uppercase tracking-wide">Aprovada</span>
                        )}
                        {reg.status === "approved" && eventoPago && reg.pagamento_confirmado_em && (
                          <span className="px-2.5 py-0.5 rounded-full bg-emerald-600 text-white font-body text-xs uppercase tracking-wide">Confirmada · pago</span>
                        )}
                        {reg.status === "approved" && eventoPago && !reg.pagamento_confirmado_em && (() => {
                          const prazo = prazoPagamento(reg);
                          return (
                            <span
                              className={`px-2.5 py-0.5 rounded-full font-body text-xs uppercase tracking-wide ${
                                prazo?.vencido ? "bg-red-600 text-white" : "bg-amber-100 text-amber-800"
                              }`}
                            >
                              {prazo?.vencido
                                ? "Prazo vencido · não pagou"
                                : `Pré-reservada · pagar até ${prazo?.fim.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) ?? "—"}`}
                            </span>
                          );
                        })()}
                        {reg.status === "waitlist" && (
                          <span className="px-2.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-body text-xs uppercase tracking-wide">Lista de espera</span>
                        )}
                        {reg.status === "rejected" && (
                          <span className="px-2.5 py-0.5 rounded-full bg-red-100 text-red-700 font-body text-xs uppercase tracking-wide">
                            {eventoPago && reg.approved_at ? "Pré-reserva cancelada" : "Recusada"}
                          </span>
                        )}
                        {reg.attendance_confirmed && (
                          <span className="px-2.5 py-0.5 rounded-full bg-primary/10 text-primary font-body text-xs">
                            <div className="w-3 h-3 inline-flex items-center justify-center mr-1">
                              <i className="ri-check-line" />
                            </div>
                            Presença confirmada
                          </span>
                        )}
                      </div>
                      <p className="font-body text-sm text-primary mb-1">{reg.profession}</p>
                      <div className="flex flex-wrap items-center gap-3 text-xs font-body text-brown/50">
                        <span className="flex items-center gap-1">
                          <div className="w-3.5 h-3.5 flex items-center justify-center">
                            <i className="ri-mail-line" />
                          </div>
                          {reg.email}
                        </span>
                        {reg.whatsapp && (
                          <span className="flex items-center gap-1">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-whatsapp-line" />
                            </div>
                            {reg.country_code ? `${reg.country_code} ` : ""}{reg.whatsapp}
                            {isInvalidWhatsapp(reg.country_code, reg.whatsapp) && (
                              <span
                                className="ml-1 px-1.5 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px]"
                                title="Telefone incompleto ou inválido — confirme com a inscrita"
                              >
                                <i className="ri-error-warning-line" /> incompleto
                              </span>
                            )}
                          </span>
                        )}
                        {reg.city && (
                          <span className="flex items-center gap-1">
                            <div className="w-3.5 h-3.5 flex items-center justify-center">
                              <i className="ri-map-pin-line" />
                            </div>
                            {reg.city}
                          </span>
                        )}
                      </div>
                      <p className="font-body text-xs text-brown/40 mt-1">
                        Inscrita em {new Date(reg.created_at).toLocaleString("pt-BR")}
                      </p>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 shrink-0">
                      {(reg.status === "pending" || reg.status === "waitlist") && (
                        <>
                          <button
                            onClick={() => openEditRegistration(reg)}
                            className="px-3 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-edit-line" />
                            </div>
                            Editar
                          </button>
                          <button
                            onClick={() => approveRegistration(reg.id)}
                            disabled={lotado}
                            title={lotado ? "Todas as vagas estão ocupadas" : "Segura a vaga por 48 h e manda os dados do Zelle"}
                            className="px-4 py-2 rounded-lg bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-check-line" />
                            </div>
                            Pré-aprovar
                          </button>
                          <button
                            onClick={() => rejectRegistration(reg.id)}
                            className="px-4 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-close-line" />
                            </div>
                            Recusar
                          </button>
                        </>
                      )}
                      {reg.status === "approved" && (
                        <>
                          {eventoPago && !reg.pagamento_confirmado_em && !reg.whatsapp_pre_reserva_em && (
                            <button
                              onClick={() => reenviarPreReserva(reg)}
                              className="px-4 py-2 rounded-lg bg-white border border-red-200 text-red-700 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                                <i className="ri-whatsapp-line" />
                              </div>
                              Tentar WhatsApp de novo
                            </button>
                          )}
                          {eventoPago && !reg.pagamento_confirmado_em && (
                            <button
                              onClick={() => confirmarPagamento(reg)}
                              className="px-4 py-2 rounded-lg bg-emerald-600 text-white font-body text-sm hover:bg-emerald-700 transition-colors whitespace-nowrap cursor-pointer"
                            >
                              <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                                <i className="ri-money-dollar-circle-line" />
                              </div>
                              Confirmar pagamento
                            </button>
                          )}
                          {eventoPago && (
                          <button
                            onClick={() => copiarLinkPagamento(reg)}
                            className="px-3 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-links-line" />
                            </div>
                            Copiar link de pagamento
                          </button>
                          )}
                          <button
                            onClick={() => openEditRegistration(reg)}
                            className="px-3 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-edit-line" />
                            </div>
                            Editar
                          </button>
                          {/* lembrete: em evento pago, só para quem já pagou */}
                          {(!eventoPago || reg.pagamento_confirmado_em) && (
                          <button
                            onClick={() => resendReminder(reg)}
                            className="px-4 py-2 rounded-lg bg-amber-100 text-amber-700 font-body text-sm hover:bg-amber-200 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-send-plane-line" />
                            </div>
                            Reenviar lembrete
                          </button>
                          )}
                          {!eventoPago && (
                            <button
                              onClick={() => rejectRegistration(reg.id)}
                              className="px-4 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Recusar
                            </button>
                          )}
                          {eventoPago && !reg.pagamento_confirmado_em && (
                            <button
                              onClick={() => cancelarPreReserva(reg)}
                              title="Libera a vaga (sem mensagem automática para ela)"
                              className="px-4 py-2 rounded-lg bg-white border border-nude text-brown font-body text-sm hover:bg-beige transition-colors whitespace-nowrap cursor-pointer"
                            >
                              Cancelar pré-reserva
                            </button>
                          )}
                        </>
                      )}
                      {reg.status === "rejected" && eventoPago && reg.approved_at && (
                        reg.aviso_cancelamento_em ? (
                          <span className="px-3 py-2 rounded-lg bg-beige font-body text-xs text-brown/60 whitespace-nowrap">
                            <i className="ri-check-line mr-1" />Aviso enviado em {new Date(reg.aviso_cancelamento_em).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                          </span>
                        ) : (
                          <button
                            onClick={() => avisarCancelamento(reg)}
                            title="Manda pelo WhatsApp que a pré-reserva foi cancelada por falta de pagamento"
                            className="px-4 py-2 rounded-lg bg-white border border-red-200 text-red-700 font-body text-sm hover:bg-red-50 transition-colors whitespace-nowrap cursor-pointer"
                          >
                            <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                              <i className="ri-whatsapp-line" />
                            </div>
                            Enviar aviso de cancelamento
                          </button>
                        )
                      )}
                      {reg.status === "rejected" && (
                        <button
                          onClick={() => approveRegistration(reg.id)}
                          disabled={lotado}
                          title={lotado ? "Todas as vagas estão ocupadas" : undefined}
                          className="px-4 py-2 rounded-lg bg-primary text-white font-body text-sm hover:bg-primary-dark transition-colors whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <div className="w-4 h-4 inline-flex items-center justify-center mr-1">
                            <i className="ri-check-line" />
                          </div>
                          Pré-aprovar
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}